/**
 * 分子话题趋势组件
 * 设计稿：6条折线图，使用指定颜色
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import { pieIcon, barIcon, lineIcon, tableIcon } from '@op/constants/chart';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_U } from '@op/utils/format';
import { useSubTopicTrend } from '@op/pages/SecurityWeeklyReport/hooks/useSubTopicTrend';
import { formatContributionText } from '@op/pages/SecurityWeeklyReport/utils/calculations';
import TrendChart from './TrendChart';
import PieChart from './PieChart';
import TableChart from './TableChart';

interface ISubTopicTrendProps {
  filter: any; 
  selectedSubTopic: string | null;
  onSubTopicClick: (name: string, category: string, sub_category: string) => void; 
}

const SubTopicTrend: React.FC<ISubTopicTrendProps> = ({
  filter, 
  selectedSubTopic,
  onSubTopicClick, 
}) => {
  const { formatMessage, isEn } = useLanguage();
  const [chartType, setChartType] = useState<'line' | 'bar' | 'pie' | 'table'>(
    'line',
  );

  // 使用 useSubTopicTrend hook，获取图表数据
  const { loading, lineChartData, pieChartData, summary, nameMap } =
    useSubTopicTrend(filter);



  const getDisplayName = useCallback(
    (nameEn: string): string => {
      const { en, cn } = nameMap?.[nameEn] || { en: nameEn, cn: nameEn };
      return isEn ? en : cn;
    },
    [isEn, nameMap],
  );

  // 图表点击事件 - 饼图和其他图表 params 结构不同
  const handleChartClick = useCallback(
    (params: any) => {
      const clickedName = chartType === 'pie' ? params.name : params.seriesName;
      if (clickedName) {
        const info = nameMap?.[clickedName];
        onSubTopicClick(
          clickedName,
          info?.category || '',
          info?.sub_category || '',
        );
      }
    },
    [onSubTopicClick, chartType, nameMap],
  );

  // 下载处理
  const handleDownload = () => {
    if (!lineChartData?.tableRecord) return;
    DBO_U.download({
      key: 'WeeklySubTopic',
      fileName: 'Weekly Report - Sub-topic Trend',
      filter,
      auxiliaryParams: { filter, columns: lineChartData.legend },
      data: lineChartData.tableRecord,
    });
  };

  // 波动贡献率描述 - 使用通用函数
  const contributionText = useMemo(() => {
    if (!summary || !filter) return '';
    const topSubCategoryName = isEn
      ? summary.sub_category_en
      : summary.sub_category_cn;
    return formatContributionText({
      categoryName: topSubCategoryName,
      changeRate: summary.fluctuation_rate,
      contributionRate: summary.contribution_rate,
      formatMessage,
    });
  }, [summary, formatMessage, isEn, filter]);

  // 根据图表类型渲染对应组件
  const renderContent = () => {
    if (chartType === 'table') {
      return (
        <TableChart data={lineChartData} getDisplayName={getDisplayName} />
      );
    }

    if (chartType === 'pie') {
      return (
        <PieChart
          data={pieChartData}
          height={400}
          selectedType={selectedSubTopic}
          onChartClick={handleChartClick}
          getDisplayName={getDisplayName}
        />
      );
    }

    // line, bar
    return (
      <TrendChart
        data={lineChartData}
        height={400}
        chartType={chartType}
        filter={filter}
        selectedType={selectedSubTopic}
        onChartClick={handleChartClick}
        getDisplayName={getDisplayName}
      />
    );
  };

  return (
    <CardCommon
      className="dbo-sub-topic-trend"
      title={
        <span>
          {formatMessage({ id: 'Mentions by Subtopic' })}
        </span>
      }
      subTitle={contributionText}
      operations={[pieIcon, barIcon, lineIcon, tableIcon]}
      defaultOperation={chartType}
      handleOperationsOnClick={(item) => {
        setChartType((item?.symbol || 'line') as 'line');
      }}
      loading={loading}
      loadingHeight={400}
      emptyHeight={400}
      empty={lineChartData?.isEmpty}
      hasDownload={!loading}
      handleDownloadOnClick={handleDownload}
      logBtnId="WR_SubTopic"
      contentCard
    >
      {renderContent()}
    </CardCommon>
  );
};

export default SubTopicTrend;
